import * as React from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Toolbar from "@mui/material/Toolbar";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useThemeContext } from "../context/ThemeContext";
import { hasPermission } from "../utils/permissions";
import { getUserInfo } from "../utils/authStorage";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const Root = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

function ClippedDrawer() {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeContext();
  const [userPermissions, setUserPermissions] = useState([]);

  const pathParts = window.location.pathname.split("/");
  const location = pathParts[1];
  const subPath = pathParts[3];

  const userInfo = getUserInfo() || {};
  const id = userInfo._id;
  const userType = (userInfo.userType || userInfo.role || "").toString().toLowerCase();
  
  // Initialize permissions on mount
  useEffect(() => {
    const initialPermissions = userInfo.permissions || [];
    setUserPermissions(initialPermissions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for permission updates
  useEffect(() => {
    const handlePermissionsUpdate = (event) => {
      setUserPermissions(event.detail.permissions || []);
    };
    
    window.addEventListener('permissionsUpdated', handlePermissionsUpdate);
    return () => window.removeEventListener('permissionsUpdated', handlePermissionsUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allMenuItems = [
    {
      label: "Dashboard",
      path: `/profile/${id}/dashboard`,
      icon: <DashboardRoundedIcon />,
      permission: "dashboard"
    },
    {
      label: "Jobs",
      path: "/jobs",
      icon: <DescriptionOutlinedIcon />,
      permission: "jobs"
    },
    {
      label: "Stock",
      path: "/jobsearch",
      icon: <ViewInArIcon />,
      permission: "stock"
    },
    {
      label: "Suppliers",
      path: "/projects",
      icon: <LocalShippingRoundedIcon />,
      permission: "suppliers"
    },
    {
      label: "Invoice",
      path: "/invoices",
      icon: <ReceiptLongIcon />,
      permission: "invoice"
    },
    {
      label: "Income",
      path: "/income",
      icon: <TrendingUpIcon />,
      permission: "income"
    },
    {
      label: "Users",
      path: "/users",
      icon: <PeopleOutlineIcon />,
      permission: "users"
    },
  ];

  // Filter menu items based on role and permissions
  let menuItems = allMenuItems;
  
  // If user has permissions defined, use permission-based filtering
  if (userPermissions && userPermissions.length > 0) {
    menuItems = allMenuItems.filter(item => 
      hasPermission(userPermissions, item.permission)
    );
  } else {
    // Fallback to role-based filtering for backward compatibility
    let allowedLabels = null;
    if (userType === "cashier") {
      allowedLabels = ["Dashboard", "Jobs", "Stock", "Suppliers", "Invoice"];
    } else if (userType === "technician") {
      allowedLabels = ["Dashboard", "Jobs"];
    }

    menuItems = allowedLabels
      ? allMenuItems.filter((m) => allowedLabels.includes(m.label))
      : allMenuItems;
  }

  const drawer = () => (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 240,
          boxSizing: "border-box",
          backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
          borderRight: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb",
        },
      }}
    >
      <Toolbar />

      <Box px={2} pt={2}>
        <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {menuItems.map((item) => {
            let isActive = false;

            if (item.label === "Dashboard") {
              isActive = location === "profile" && subPath === "dashboard";
            } else {
              isActive = window.location.pathname === item.path;
            }

            return (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: "12px",
                    px: 2,
                    py: 1.25,
                    backgroundColor: isActive 
                      ? isDarkMode ? "#0f4a8c" : "#eff6ff" 
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#334155" : "#f3f4f6",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isActive 
                        ? isDarkMode ? "#93c5fd" : "#2563eb" 
                        : isDarkMode ? "#cbd5e1" : "#111827",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontFamily: "Manrope, sans-serif",
                          fontSize: "0.95rem",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive 
                            ? isDarkMode ? "#93c5fd" : "#2563eb" 
                            : isDarkMode ? "#cbd5e1" : "#111827",
                        }}
                      >
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ mt: 2, borderColor: isDarkMode ? "#334155" : "#e5e7eb" }} />
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Root>{drawer()}</Root>
    </Box>
  );
}

export default React.memo(ClippedDrawer);
